import { useEffect } from 'react';

import { useParams } from 'react-router-dom';

import { useAppSelector, useAppDispatch } from 'hooks';

import { useQuery } from '@tanstack/react-query';

import {
  Text,
  SimpleGrid,
  Box,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';

import { useFeatureFlag } from 'hooks/useFeatureFlag';

import Card from 'components/card/Card';

import FormInput from 'components/fields/auto/FormInput';
import RadioInput from 'components/fields/auto/RadioInput';
import SelectInput from 'components/fields/auto/SelectInput';

import {
  changeReportOfBrokerAgent,
  removeMissingMandatoryField,
} from 'reducers/autoPersonalFormReducer';

import { SECTIONS } from 'views/auto_form/constants';
import {
  PROVINCE_ALBERTA,
  PROVINCE_NOVA_SCOTIA,
  isNonOntarioProvince,
} from 'variables/constants';

import { apiGetSupervisorCandidates } from 'api';

import getRadioYesNo from 'helpers/getRadioYesNo';

const YES_NO_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

const BROKER_LEVEL_1 = 'Level 1';

const BROKER_LEVEL_OPTIONS = ['Level 1', 'Level 2', 'Level 3', 'Level 4'].map(
  (level) => ({ value: level, label: level }),
);

const SUPERVISOR_LEVEL_OPTIONS = ['Level 2', 'Level 3', 'Level 4'].map(
  (level) => ({ value: level, label: level }),
);

interface SupervisorCandidate {
  id: number;
  name: string;
  email: string;
  role: number;
  role_display: string;
}

const ReportOfBrokerAgent = () => {
  const { uuid } = useParams();
  const dispatch = useAppDispatch();

  const data = useAppSelector(
    (state) => state.autoPersonalForm.data.report_of_broker_agent,
  );

  const isRemarket = useAppSelector(
    (state) => state.autoPersonalForm.data.policy_information.is_remarket,
  );

  const province = useAppSelector((state) => state.autoPersonalForm.province);
  const isNonOntario = isNonOntarioProvince(province);
  const isAlberta = province === PROVINCE_ALBERTA;
  const isNovaScotia = province === PROVINCE_NOVA_SCOTIA;

  const isLevelOne = data.broker_level === BROKER_LEVEL_1;
  const isBaraEnabled = useFeatureFlag('enable-bara');

  const referralSuffix = isBaraEnabled
    ? ' or submit an After-Hours Referral (MP/Delegate)'
    : '';

  const { data: supervisorCandidates = [] } = useQuery<SupervisorCandidate[]>({
    queryKey: ['supervisor-candidates', uuid],
    queryFn: () => apiGetSupervisorCandidates(uuid),
    enabled: isNovaScotia && Boolean(uuid),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!isNovaScotia) {
      dispatch(removeMissingMandatoryField('Level of Licence'));
    }
    if (!isLevelOne) {
      dispatch(removeMissingMandatoryField('Supervisor (Level 2+) to co-sign'));
      dispatch(removeMissingMandatoryField('Supervisor Level'));
    }
  }, [isNovaScotia, isLevelOne, dispatch]);

  const onSupervisorSelect = (event: any) => {
    const memberId = event.target.value;
    const selected = supervisorCandidates.find(
      (candidate) => String(candidate.id) === String(memberId),
    );

    dispatch(
      changeReportOfBrokerAgent({
        name: 'supervisor_member_id',
        value: memberId,
      }),
    );
    dispatch(
      changeReportOfBrokerAgent({
        name: 'supervisor_name',
        value: selected?.name ?? '',
      }),
    );
    dispatch(
      changeReportOfBrokerAgent({
        name: 'supervisor_email',
        value: selected?.email ?? '',
      }),
    );
    if (selected && !data.supervisor_level) {
      dispatch(
        changeReportOfBrokerAgent({
          name: 'supervisor_level',
          value: 'Level 2',
        }),
      );
    }
  };

  const onchangeReportOfBrokerAgentChange = (event: any) => {
    const { name, value, checked, type } = event.target;
    const payload = type === 'checkbox' ? checked : value;

    dispatch(changeReportOfBrokerAgent({ name, value: payload }));
  };

  const onBooleanChange = (name: string, value: string) => {
    dispatch(changeReportOfBrokerAgent({ name, value: value === 'true' }));
  };

  return (
    <Card mb="20px" id="sectionReportOfBrokerAgent">
      <Text fontWeight="bold" fontSize="xl">
        Report of Broker
      </Text>

      <SimpleGrid columns={2} spacing="20px" mt="20px">
        <FormInput
          section={SECTIONS.REPORT_OF_BROKER_AGENT}
          label="How long have you known the Applicant?"
          name="how_long_have_you_known_the_applicant"
          value={data.how_long_have_you_known_the_applicant}
          onChange={onchangeReportOfBrokerAgentChange}
          required={isRemarket}
        />

        {isNonOntario && (
          <FormInput
            section={SECTIONS.REPORT_OF_BROKER_AGENT}
            label="How long have you known the principal operators?"
            name="how_long_have_you_known_the_principal_driver"
            value={data.how_long_have_you_known_the_principal_driver || ''}
            onChange={onchangeReportOfBrokerAgentChange}
          />
        )}

        <FormInput
          section={SECTIONS.REPORT_OF_BROKER_AGENT}
          label="Type of Motor Vehicle Liability Insurance Card issued"
          name="type_card_issued"
          value={data.type_card_issued}
          onChange={onchangeReportOfBrokerAgentChange}
          disabled
        />
      </SimpleGrid>

      {isNonOntario && (
        <Box mt="20px">
          <RadioInput
            section={SECTIONS.REPORT_OF_BROKER_AGENT}
            label="Have you bound this risk?"
            options={YES_NO_OPTIONS}
            value={getRadioYesNo(data.is_this_risk_bound)}
            onChange={(value: string) =>
              onBooleanChange('is_this_risk_bound', value)
            }
            required
          />

          <RadioInput
            section={SECTIONS.REPORT_OF_BROKER_AGENT}
            label="Is this business new to your office?"
            options={YES_NO_OPTIONS}
            value={getRadioYesNo(data.is_this_business_new_to_you)}
            onChange={(value: string) =>
              onBooleanChange('is_this_business_new_to_you', value)
            }
            mt="10px"
            required
          />

          <RadioInput
            section={SECTIONS.REPORT_OF_BROKER_AGENT}
            label="Does your client have other insurance with this company?"
            options={YES_NO_OPTIONS}
            value={getRadioYesNo(data.has_other_insurance_with_company)}
            onChange={(value: string) =>
              onBooleanChange('has_other_insurance_with_company', value)
            }
            mt="10px"
            required
          />
          {data.has_other_insurance_with_company && (
            <Box mt="10px">
              <SimpleGrid columns={isAlberta ? 2 : 1} spacing="20px">
                {isAlberta && (
                  <FormInput
                    section={SECTIONS.REPORT_OF_BROKER_AGENT}
                    label="Policy Number"
                    name="other_insurance_policy_number"
                    value={data.other_insurance_policy_number || ''}
                    onChange={onchangeReportOfBrokerAgentChange}
                    required
                  />
                )}
                <FormInput
                  section={SECTIONS.REPORT_OF_BROKER_AGENT}
                  label="Particulars of other insurance"
                  name="other_insurance_particulars"
                  value={data.other_insurance_particulars || ''}
                  onChange={onchangeReportOfBrokerAgentChange}
                  required
                />
              </SimpleGrid>
            </Box>
          )}

          <RadioInput
            section={SECTIONS.REPORT_OF_BROKER_AGENT}
            label="Are there any special circumstances concerning this application which the company should know about?"
            options={YES_NO_OPTIONS}
            value={getRadioYesNo(data.has_special_circumstances)}
            onChange={(value: string) =>
              onBooleanChange('has_special_circumstances', value)
            }
            mt="10px"
            required
          />
          {data.has_special_circumstances && (
            <>
              <Alert status="error" mt="10px" borderRadius="md" fontSize="sm">
                <AlertIcon />
                <AlertDescription>
                  Special circumstances require review. Please refer to BIG
                  Underwriting{referralSuffix}.
                </AlertDescription>
              </Alert>
              <Box mt="10px">
                <FormInput
                  section={SECTIONS.REPORT_OF_BROKER_AGENT}
                  label="Particulars of special circumstances"
                  name="special_circumstances_particulars"
                  value={data.special_circumstances_particulars || ''}
                  onChange={onchangeReportOfBrokerAgentChange}
                  required
                />
              </Box>
            </>
          )}

          {isAlberta && (
            <>
              <RadioInput
                section={SECTIONS.REPORT_OF_BROKER_AGENT}
                label="Is this risk eligible for the residual market, but being placed in the regular market under the take-all-comers rule?"
                options={YES_NO_OPTIONS}
                value={getRadioYesNo(data.is_residual_market_take_all_comers)}
                onChange={(value: string) =>
                  onBooleanChange('is_residual_market_take_all_comers', value)
                }
                mt="10px"
                required
              />
              {data.is_residual_market_take_all_comers && (
                <>
                  <Alert
                    status="error"
                    mt="10px"
                    borderRadius="md"
                    fontSize="sm"
                  >
                    <AlertIcon />
                    <AlertDescription>
                      Risk placed under the take-all-comers rule. Please refer
                      to BIG Underwriting{referralSuffix}.
                    </AlertDescription>
                  </Alert>
                  <Box mt="10px">
                    <FormInput
                      section={SECTIONS.REPORT_OF_BROKER_AGENT}
                      label="Particulars"
                      name="residual_market_particulars"
                      value={data.residual_market_particulars || ''}
                      onChange={onchangeReportOfBrokerAgentChange}
                      required
                    />
                  </Box>
                </>
              )}

              <RadioInput
                section={SECTIONS.REPORT_OF_BROKER_AGENT}
                label="Was the Supplementary Market Availability Plan (SMAP) accessed to place this risk?"
                options={YES_NO_OPTIONS}
                value={getRadioYesNo(data.is_smap_accessed)}
                onChange={(value: string) =>
                  onBooleanChange('is_smap_accessed', value)
                }
                mt="10px"
                required
              />
              {data.is_smap_accessed && (
                <>
                  <Alert
                    status="error"
                    mt="10px"
                    borderRadius="md"
                    fontSize="sm"
                  >
                    <AlertIcon />
                    <AlertDescription>
                      SMAP was accessed to place this risk. Please refer to BIG
                      Underwriting{referralSuffix}.
                    </AlertDescription>
                  </Alert>
                  <Box mt="10px">
                    <FormInput
                      section={SECTIONS.REPORT_OF_BROKER_AGENT}
                      label="MAP reference number"
                      name="smap_reference_number"
                      value={data.smap_reference_number || ''}
                      onChange={onchangeReportOfBrokerAgentChange}
                      required
                    />
                  </Box>
                </>
              )}
            </>
          )}
        </Box>
      )}

      {isNovaScotia && (
        <Box mt="20px">
          <Text fontWeight="bold" fontSize="md" mb="10px">
            Broker Licence Level
          </Text>

          <SimpleGrid columns={2} spacing="20px">
            <SelectInput
              section={SECTIONS.REPORT_OF_BROKER_AGENT}
              label="Level of Licence"
              name="broker_level"
              options={BROKER_LEVEL_OPTIONS}
              value={data.broker_level || ''}
              onChange={onchangeReportOfBrokerAgentChange}
              placeholder="Select..."
              required
            />
          </SimpleGrid>

          {isLevelOne && (
            <SimpleGrid columns={2} spacing="20px" mt="10px">
              <SelectInput
                section={SECTIONS.REPORT_OF_BROKER_AGENT}
                label="Supervisor (Level 2+) to co-sign"
                name="supervisor_member_id"
                options={supervisorCandidates.map((candidate) => ({
                  value: String(candidate.id),
                  label: `${candidate.name} (${candidate.role_display})`,
                }))}
                value={
                  data.supervisor_member_id
                    ? String(data.supervisor_member_id)
                    : ''
                }
                onChange={onSupervisorSelect}
                placeholder="Select..."
                required
              />

              <SelectInput
                section={SECTIONS.REPORT_OF_BROKER_AGENT}
                label="Supervisor Level"
                name="supervisor_level"
                options={SUPERVISOR_LEVEL_OPTIONS}
                value={data.supervisor_level || ''}
                onChange={onchangeReportOfBrokerAgentChange}
                placeholder="Select..."
                required
              />
            </SimpleGrid>
          )}
        </Box>
      )}
    </Card>
  );
};

export default ReportOfBrokerAgent;
